import {
  faCalendar,
  faShieldHalved,
  faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';
import { type FC, memo } from 'react';

import type { Props } from '@/components/MemberEligibilityBadges/types';
import { Pill, Tooltip } from '@/ui';
import { formatCalendarDate } from '@/utils/dates';

const MemberEligibilityBadges: FC<Props> = ({
  conflictOfInterestReason,
  hasConflictOfInterest,
  ineligibilityReason,
  isCredentialed,
  isEligible,
  isExisting,
  otherGroupNames,
  startDate
}) => {
  const hasOtherGroups = !!otherGroupNames?.length;
  if (
    !startDate &&
    isEligible !== false &&
    isCredentialed !== false &&
    !hasConflictOfInterest &&
    !hasOtherGroups
  ) {
    return null;
  }

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {startDate && (
        <Pill color="neutral" icon={faCalendar} size="sm" variant="subtle">
          Since {formatCalendarDate(startDate)}
        </Pill>
      )}
      {isEligible === false && (
        <Tooltip
          content={
            ineligibilityReason ?? "This person does not match the group's eligibility criteria."
          }>
          <Pill color="warning" icon={faTriangleExclamation} size="sm" variant="subtle">
            {isExisting ? 'No longer eligible' : 'Not eligible'}
          </Pill>
        </Tooltip>
      )}
      {isCredentialed === false && (
        <Tooltip content="One or more session providers are not credentialed for this member.">
          <Pill color="destructive" icon={faShieldHalved} size="sm" variant="subtle">
            Provider not Credentialed
          </Pill>
        </Tooltip>
      )}
      {hasConflictOfInterest && (
        <Tooltip
          content={
            conflictOfInterestReason ?? 'This person has a conflict of interest with this group.'
          }>
          <Pill color="destructive" icon={faTriangleExclamation} size="sm" variant="subtle">
            Conflict of interest
          </Pill>
        </Tooltip>
      )}
      {hasOtherGroups && (
        <Tooltip content={`Already a member of: ${otherGroupNames.join(', ')}`}>
          <Pill color="warning" icon={faTriangleExclamation} size="sm" variant="subtle">
            In another group
          </Pill>
        </Tooltip>
      )}
    </div>
  );
};

export default memo(MemberEligibilityBadges);
